import React from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import UndoIcon from '@mui/icons-material/Undo';
import EditIcon from '@mui/icons-material/Edit';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import Swal from 'sweetalert2';
import { etiquetaDiaCompleto, formatearMinutos } from './calendarioUtils';
import { TareaDialog } from '../proyectos/tareas/TareaDialog';
import { isColorDark, ordenarOpcionesPorLabel, textoMayusculas } from '../../utils/utils';
import { deleteCitaCalendario, getClientes, getTrabajadores, postMoverEventoCalendario, reactivarCitaCalendario, updateCitaCalendario } from '../../services/apis';

const InfoItem = ({ icon, label, value }) => (
    <Box
        sx={{
            display: 'flex',
            gap: 1,
            height: '100%',
            p: 1.25,
            borderRadius: '8px',
            border: '1px solid rgba(0,0,0,0.12)',
            backgroundColor: '#fff',
        }}
    >
        <Box sx={{ color: 'var(--principal)', mt: 0.15 }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                {label}
            </Typography>
            <Typography sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                {value || '-'}
            </Typography>
        </Box>
    </Box>
);

const convertirIsoAIdDia = (fechaIso) => {
    const [year, month, day] = String(fechaIso || '').split('-');
    return day && month && year ? `${day}-${month}-${year}` : '';
};

const convertirTrabajadoresAOptions = (trabajadores = '') =>
    String(trabajadores || '')
        .split(',')
        .map((trabajador) => trabajador.trim())
        .filter(Boolean)
        .map((trabajador) => ({
            label: trabajador,
            value: trabajador,
        }));

const convertirClienteAOption = (cliente = '') => {
    const nombreCliente = String(cliente || '').trim();

    return nombreCliente
        ? {
            label: nombreCliente,
            value: nombreCliente,
        }
        : null;
};

const normalizarNombreTrabajador = (nombre = '') =>
    String(nombre || '').trim().toLocaleLowerCase('es-ES');

const TELEFONOS_RECORDATORIO_WHATSAPP = [
    { label: 'Administracion', telefono: '636710447' },
    { label: 'Agora TPV', telefono: '611993251' },
    { label: 'Desarrollo', telefono: '611902562' },
    { label: 'Programacion', telefono: '621188740' },
    { label: 'Tecnicos', telefono: '621171103' },
];

const normalizarTelefonoWhatsapp = (telefono) => {
    let telefonoLimpio = String(telefono || '').replace(/\D/g, '');

    if (telefonoLimpio.startsWith('00')) {
        telefonoLimpio = telefonoLimpio.slice(2);
    }

    if (telefonoLimpio.length === 9) {
        return `34${telefonoLimpio}`;
    }

    return telefonoLimpio;
};

export const EventoTareaDialog = ({ open, onClose, evento, onChanged }) => {
    const [openTarea, setOpenTarea] = React.useState(false);
    const [reagendando, setReagendando] = React.useState(false);
    const [editando, setEditando] = React.useState(false);
    const [fechaReagendar, setFechaReagendar] = React.useState('');
    const [asuntoEditado, setAsuntoEditado] = React.useState('');
    const [horaEditada, setHoraEditada] = React.useState('');
    const [opClientes, setOpClientes] = React.useState([]);
    const [clienteEditado, setClienteEditado] = React.useState(null);
    const [opTrabajadores, setOpTrabajadores] = React.useState([]);
    const [trabajadoresEditados, setTrabajadoresEditados] = React.useState([]);
    const [openWhatsapp, setOpenWhatsapp] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    const swalPorEncimaDelDialog = {
        didOpen: () => {
            const swalContainer = document.querySelector('.swal2-container');
            if (swalContainer) {
                swalContainer.style.zIndex = '20000';
            }
        },
    };

    React.useEffect(() => {
        if (open && evento) {
            setReagendando(false);
            setEditando(false);
            setSaving(false);
            setFechaReagendar(evento.fecha_iso || '');
            setAsuntoEditado(evento.titulo || '');
            setHoraEditada(evento.hora_inicio || '');
            setClienteEditado(convertirClienteAOption(evento.cliente));
            setTrabajadoresEditados(convertirTrabajadoresAOptions(evento.trabajadores_asignados));
            setOpenWhatsapp(false);
        }
    }, [open, evento]);

    React.useEffect(() => {
        const cargarClientes = async () => {
            if (!open || opClientes.length) return;

            const response = await getClientes();
            const opciones = ordenarOpcionesPorLabel((response?.data || []).map((item) => ({
                label: textoMayusculas(item.nombre),
                value: item.id,
            })));

            setOpClientes(opciones);
        };

        cargarClientes();
    }, [open, opClientes.length]);

    React.useEffect(() => {
        const cargarTrabajadores = async () => {
            if (!open || opTrabajadores.length) return;

            const response = await getTrabajadores();
            const opciones = ordenarOpcionesPorLabel((response?.data || []).map((item) => ({
                label: textoMayusculas(item.nombre),
                value: item.id,
                email: item.mail || item.email || item.correo || '',
            })));

            setOpTrabajadores(opciones);
        };

        cargarTrabajadores();
    }, [open, opTrabajadores.length]);

    if (!evento) return null;

    const color = evento.color_estado || '#e53935';
    const textColor = isColorDark(color) ? '#fff' : '#000';
    const esCita = evento.tipo_calendario === 'cita';
    const idCita = evento.id_cita || evento.id;
    const citaFinalizada = esCita && Number(evento.finalizado || 0) === 1;
    const estilosBotonRecordatorio = {
        borderRadius: '14px',
        fontWeight: 700,
        textTransform: 'none',
        px: 1.15,
        py: 0.25,
        minHeight: 28,
        height: 28,
        fontSize: 12,
        lineHeight: 1,
        '& .MuiButton-startIcon': {
            mr: 0.6,
        },
        '& .MuiSvgIcon-root': {
            fontSize: 16,
        },
    };

    const handleEliminarCita = async () => {
        const confirm = await Swal.fire({
            icon: 'warning',
            title: 'Eliminar recordatorio',
            text: 'Esta cita dejara de mostrarse en el calendario.',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d32f2f',
            ...swalPorEncimaDelDialog,
        });

        if (!confirm.isConfirmed) return;

        setSaving(true);
        const response = await deleteCitaCalendario(idCita);
        setSaving(false);

        if (!response?.success) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo eliminar',
                text: response?.message || 'Revisa la cita e intentalo de nuevo.',
                ...swalPorEncimaDelDialog,
            });
            return;
        }

        if (onChanged) {
            await onChanged();
        }

        onClose();
    };

    const handleReagendarCita = async () => {
        const fecha = convertirIsoAIdDia(fechaReagendar);

        if (!fecha) {
            Swal.fire({
                icon: 'warning',
                title: 'Fecha no valida',
                text: 'Selecciona una fecha para reagendar la cita.',
                ...swalPorEncimaDelDialog,
            });
            return;
        }

        setSaving(true);
        const response = await postMoverEventoCalendario({
            tipo: 'cita',
            id: idCita,
            fecha,
        });
        setSaving(false);

        if (!response?.success) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo reagendar',
                text: response?.message || 'Revisa la fecha e intentalo de nuevo.',
                ...swalPorEncimaDelDialog,
            });
            return;
        }

        if (onChanged) {
            await onChanged();
        }

        onClose();
    };

    const handleReactivarCita = async () => {
        const confirm = await Swal.fire({
            icon: 'question',
            title: 'Reactivar cita',
            text: 'La cita volvera a quedar agendada en el calendario.',
            showCancelButton: true,
            confirmButtonText: 'Reactivar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#1976d2',
            ...swalPorEncimaDelDialog,
        });

        if (!confirm.isConfirmed) return;

        setSaving(true);
        const response = await reactivarCitaCalendario(idCita);
        setSaving(false);

        if (!response?.success) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo reactivar',
                text: response?.message || 'Revisa la cita e intentalo de nuevo.',
                ...swalPorEncimaDelDialog,
            });
            return;
        }

        if (onChanged) {
            await onChanged();
        }

        onClose();
    };

    const handleEditarCita = async () => {
        const asunto = asuntoEditado.trim();

        if (!asunto) {
            Swal.fire({
                icon: 'warning',
                title: 'Nombre obligatorio',
                text: 'La cita necesita un nombre para poder guardarse.',
                ...swalPorEncimaDelDialog,
            });
            return;
        }

        setSaving(true);
        const response = await updateCitaCalendario(idCita, {
            asunto,
            fecha: evento.fecha_iso,
            hora: horaEditada,
            cliente: clienteEditado,
            trabajadores: trabajadoresEditados,
        });
        setSaving(false);

        if (!response?.success) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo editar',
                text: response?.message || 'Revisa la cita e intentalo de nuevo.',
                ...swalPorEncimaDelDialog,
            });
            return;
        }

        if (onChanged) {
            await onChanged();
        }

        onClose();
    };

    const obtenerEmailsTrabajadoresAsignados = async () => {
        let opcionesTrabajadores = opTrabajadores;

        if (!opcionesTrabajadores.length) {
            const response = await getTrabajadores();
            opcionesTrabajadores = ordenarOpcionesPorLabel((response?.data || []).map((item) => ({
                label: textoMayusculas(item.nombre),
                value: item.id,
                email: item.mail || item.email || item.correo || '',
            })));
            setOpTrabajadores(opcionesTrabajadores);
        }

        const trabajadoresAsignados = trabajadoresEditados.length
            ? trabajadoresEditados
            : convertirTrabajadoresAOptions(evento.trabajadores_asignados);

        return trabajadoresAsignados
            .map((trabajador) => {
                if (trabajador?.email) return trabajador.email;

                const trabajadorEncontrado = opcionesTrabajadores.find((opcion) =>
                    normalizarNombreTrabajador(opcion.label) === normalizarNombreTrabajador(trabajador?.label)
                );

                return trabajadorEncontrado?.email || '';
            })
            .filter(Boolean)
            .filter((email, index, array) => array.indexOf(email) === index);
    };

    const handleCorreoTrabajadores = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const trabajadoresAsignados = trabajadoresEditados.length
            ? trabajadoresEditados
            : convertirTrabajadoresAOptions(evento.trabajadores_asignados);

        if (!trabajadoresAsignados.length) {
            Swal.fire({
                icon: 'info',
                title: 'Sin trabajadores asignados',
                text: 'Esta cita no tiene trabajadores asignados.',
                ...swalPorEncimaDelDialog,
            });
            return;
        }

        const emails = await obtenerEmailsTrabajadoresAsignados();

        if (!emails.length) {
            Swal.fire({
                icon: 'warning',
                title: 'Sin correos disponibles',
                text: 'Los trabajadores asignados no tienen correo disponible para abrir el email.',
                ...swalPorEncimaDelDialog,
            });
            return;
        }

        const asunto = encodeURIComponent(`Recordatorio: ${evento.titulo || ''}`);
        window.location.href = `mailto:${emails.join(',')}?subject=${asunto}`;
    };

    const handleAbrirWhatsapp = (telefono) => {
        const telefonoNormalizado = normalizarTelefonoWhatsapp(telefono);

        if (!telefonoNormalizado) {
            Swal.fire({
                icon: 'warning',
                title: 'Telefono no configurado',
                text: 'Este numero de WhatsApp todavia no esta configurado.',
                ...swalPorEncimaDelDialog,
            });
            return;
        }

        const texto = encodeURIComponent(
            `Recordatorio de cita: ${evento.titulo || ''}\n\nHola, envío recordatorio de esta cita.`
        );

        window.open(`https://wa.me/${telefonoNormalizado}?text=${texto}`, '_blank');
        setOpenWhatsapp(false);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontWeight: 700, pb: 1.2 }}>
                    {esCita ? 'Detalle de la cita' : 'Detalle de tarea planificada'}
                </DialogTitle>

                <DialogContent dividers sx={{ py: 2 }}>
                    <Stack spacing={2}>
                        <Box
                            sx={{
                                borderRadius: '8px',
                                border: '1px solid rgba(0,0,0,0.12)',
                                backgroundColor: 'rgba(229,57,53,0.05)',
                                p: 2,
                            }}
                        >
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.2}
                                justifyContent="space-between"
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    {!esCita && (
                                        <Typography variant="overline" sx={{ fontWeight: 700 }}>
                                            {`Tarea #${evento.id}`}
                                        </Typography>
                                    )}
                                    {editando && esCita ? (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Nombre de la cita"
                                            value={asuntoEditado}
                                            onChange={(event) => setAsuntoEditado(event.target.value)}
                                            sx={{ mt: 0.8, backgroundColor: '#fff' }}
                                        />
                                    ) : (
                                        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.4 }}>
                                            {evento.titulo}
                                        </Typography>
                                    )}
                                    <Typography sx={{ mt: 0.6, color: 'text.secondary' }}>
                                        {etiquetaDiaCompleto(evento.fecha)}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap' }}>
                                    {esCita ? (
                                        <>
                                            <Tooltip title="Enviar correo a trabajadores asignados">
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<EmailIcon />}
                                                    onClick={handleCorreoTrabajadores}
                                                    sx={{
                                                        ...estilosBotonRecordatorio,
                                                        backgroundColor: color,
                                                        color: '#fff',
                                                        '& .MuiSvgIcon-root': {
                                                            ...estilosBotonRecordatorio['& .MuiSvgIcon-root'],
                                                            color: '#fff',
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: color,
                                                            filter: 'brightness(0.95)',
                                                        },
                                                    }}
                                                >
                                                    {evento.estado || 'Recordatorio'}
                                                </Button>
                                            </Tooltip>

                                            <Tooltip title="Enviar recordatorio por WhatsApp">
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<WhatsAppIcon />}
                                                    onClick={() => setOpenWhatsapp(true)}
                                                    sx={{
                                                        ...estilosBotonRecordatorio,
                                                        backgroundColor: '#25D366',
                                                        color: '#fff',
                                                        '&:hover': {
                                                            backgroundColor: '#20bd5a',
                                                        },
                                                    }}
                                                >
                                                    WhatsApp
                                                </Button>
                                            </Tooltip>
                                        </>
                                    ) : evento.estado && (
                                        <Chip
                                            label={evento.estado}
                                            sx={{
                                                backgroundColor: color,
                                                color: textColor,
                                                fontWeight: 700,
                                            }}
                                        />
                                    )}
                                    {esCita && evento.agendada_desde_tarea && (
                                        <Chip
                                            icon={<EventAvailableIcon />}
                                            label={`Agendada desde tarea #${evento.id_tarea_origen}`}
                                            variant="outlined"
                                            sx={{ fontWeight: 700 }}
                                        />
                                    )}
                                    {!esCita && (
                                        <Chip
                                            variant="outlined"
                                            label={`${evento.hora_inicio} - ${evento.hora_final}`}
                                            sx={{ fontWeight: 700 }}
                                        />
                                    )}
                                </Stack>
                            </Stack>
                        </Box>

                        <Grid container spacing={1.2}>
                            {esCita ? (
                                <>
                                    {evento.cliente && (
                                        <Grid item xs={12}>
                                            <InfoItem icon={<BusinessIcon />} label="Cliente" value={evento.cliente} />
                                        </Grid>
                                    )}

                                    {evento.agendada_desde_tarea && (
                                        <Grid item xs={12}>
                                            <InfoItem
                                                icon={<EventAvailableIcon />}
                                                label="Origen"
                                                value={`Agendada desde la tarea #${evento.id_tarea_origen}`}
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12} sm={6}>
                                        <InfoItem
                                            icon={<AccessTimeIcon />}
                                            label="Hora"
                                            value={evento.hora_inicio}
                                        />
                                    </Grid>

                                    {evento.trabajadores_asignados && (
                                        <Grid item xs={12} sm={6}>
                                            <InfoItem
                                                icon={<AssignmentIcon />}
                                                label="Trabajadores asignados"
                                                value={evento.trabajadores_asignados}
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: '8px',
                                                border: '1px solid rgba(0,0,0,0.12)',
                                                backgroundColor: '#fff',
                                            }}
                                        >
                                            <Stack spacing={1.4}>
                                                <Stack
                                                    direction={{ xs: 'column', sm: 'row' }}
                                                    spacing={1}
                                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<DeleteOutlineIcon />}
                                                        onClick={handleEliminarCita}
                                                        disabled={saving}
                                                    >
                                                        Eliminar cita
                                                    </Button>
                                                    <Button
                                                        variant={editando ? 'contained' : 'outlined'}
                                                        color="primary"
                                                        startIcon={<EditIcon />}
                                                        onClick={() => {
                                                            setEditando((prev) => !prev);
                                                            setReagendando(false);
                                                        }}
                                                        disabled={saving}
                                                    >
                                                        Editar
                                                    </Button>
                                                    {citaFinalizada ? (
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            startIcon={<UndoIcon />}
                                                            onClick={handleReactivarCita}
                                                            disabled={saving}
                                                        >
                                                            Reactivar cita
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            variant={reagendando ? 'contained' : 'outlined'}
                                                            startIcon={<EventRepeatIcon />}
                                                            onClick={() => setReagendando((prev) => !prev)}
                                                            disabled={saving}
                                                        >
                                                            Reagendar
                                                        </Button>
                                                    )}
                                                </Stack>

                                                {citaFinalizada && (
                                                    <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                                        Esta cita esta finalizada y queda solo como consulta.
                                                    </Typography>
                                                )}

                                                {editando && (
                                                    <Stack
                                                        direction={{ xs: 'column', sm: 'row' }}
                                                        spacing={1}
                                                        alignItems={{ xs: 'stretch', sm: 'center' }}
                                                        sx={{ flexWrap: { sm: 'wrap' } }}
                                                    >
                                                        <TextField
                                                            size="small"
                                                            label="Nombre de la cita"
                                                            value={asuntoEditado}
                                                            onChange={(event) => setAsuntoEditado(event.target.value)}
                                                            fullWidth
                                                        />
                                                        <TextField
                                                            size="small"
                                                            type="time"
                                                            label="Hora"
                                                            value={horaEditada}
                                                            onChange={(event) => setHoraEditada(event.target.value)}
                                                            InputLabelProps={{ shrink: true }}
                                                            helperText="Opcional"
                                                            sx={{ minWidth: { sm: 150 } }}
                                                        />
                                                        <Autocomplete
                                                            options={opClientes}
                                                            value={clienteEditado}
                                                            onChange={(event, value) => setClienteEditado(value)}
                                                            getOptionLabel={(option) => option?.label || ''}
                                                            isOptionEqualToValue={(option, value) =>
                                                                String(option.value) === String(value.value)
                                                                    || String(option.label || '').toLocaleLowerCase('es-ES') === String(value.label || '').toLocaleLowerCase('es-ES')
                                                            }
                                                            sx={{ flex: 1, minWidth: { sm: 220 } }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    label="Cliente"
                                                                    helperText="Opcional"
                                                                />
                                                            )}
                                                        />
                                                        <Autocomplete
                                                            multiple
                                                            options={opTrabajadores}
                                                            value={trabajadoresEditados}
                                                            onChange={(event, value) => setTrabajadoresEditados(value)}
                                                            getOptionLabel={(option) => option?.label || ''}
                                                            isOptionEqualToValue={(option, value) =>
                                                                String(option.value) === String(value.value)
                                                                    || String(option.label || '').toLocaleLowerCase('es-ES') === String(value.label || '').toLocaleLowerCase('es-ES')
                                                            }
                                                            sx={{ flex: 1, minWidth: { sm: 260 } }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    label="Trabajadores"
                                                                    helperText="Opcional"
                                                                />
                                                            )}
                                                        />
                                                        <Button
                                                            variant="contained"
                                                            onClick={handleEditarCita}
                                                            disabled={saving}
                                                            sx={{ minWidth: 132 }}
                                                        >
                                                            {saving ? 'Guardando...' : 'Guardar'}
                                                        </Button>
                                                    </Stack>
                                                )}

                                                {reagendando && !citaFinalizada && (
                                                    <Stack
                                                        direction={{ xs: 'column', sm: 'row' }}
                                                        spacing={1}
                                                        alignItems={{ xs: 'stretch', sm: 'center' }}
                                                    >
                                                        <TextField
                                                            type="date"
                                                            size="small"
                                                            label="Nueva fecha"
                                                            value={fechaReagendar}
                                                            onChange={(event) => setFechaReagendar(event.target.value)}
                                                            InputLabelProps={{ shrink: true }}
                                                            fullWidth
                                                        />
                                                        <Button
                                                            variant="contained"
                                                            onClick={handleReagendarCita}
                                                            disabled={saving}
                                                            sx={{ minWidth: 132 }}
                                                        >
                                                            {saving ? 'Guardando...' : 'Guardar'}
                                                        </Button>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </Box>
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <InfoItem
                                            icon={<AccessTimeIcon />}
                                            label="Duracion estimada"
                                            value={formatearMinutos(evento.minutos_estimados)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <InfoItem icon={<FolderIcon />} label="Proyecto" value={evento.proyecto} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <InfoItem icon={<BusinessIcon />} label="Cliente" value={evento.cliente} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <InfoItem icon={<EmailIcon />} label="Correo" value={evento.correo} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={8}>
                                        <InfoItem
                                            icon={<AssignmentIcon />}
                                            label="Descripcion"
                                            value={evento.descripcion}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 2.5, py: 1.7 }}>
                    <Button onClick={onClose} variant="outlined">
                        Cerrar
                    </Button>
                    {!esCita && (
                        <Button onClick={() => setOpenTarea(true)} variant="contained">
                            Abrir tarea
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {!esCita && (
                <TareaDialog
                    open={openTarea}
                    setOpen={setOpenTarea}
                    tareaInfo={evento}
                    backgroundCard={color}
                    textColor={textColor}
                    handleEvent={() => {}}
                />
            )}

            <Dialog
                open={openWhatsapp}
                onClose={() => setOpenWhatsapp(false)}
                fullWidth
                maxWidth="xs"
                sx={{
                    '& .MuiDialog-container': {
                        alignItems: 'center',
                    },
                    '& .MuiPaper-root': {
                        zIndex: 15000,
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                    Recordatorio por WhatsApp
                </DialogTitle>
                <DialogContent dividers sx={{ py: 1.5 }}>
                    <Stack spacing={1}>
                        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>
                            Selecciona el numero al que quieres enviar el recordatorio.
                        </Typography>

                        {TELEFONOS_RECORDATORIO_WHATSAPP.map((item) => {
                            const telefonoNormalizado = normalizarTelefonoWhatsapp(item.telefono);

                            return (
                                <Button
                                    key={item.label}
                                    variant="outlined"
                                    startIcon={<WhatsAppIcon />}
                                    onClick={() => handleAbrirWhatsapp(item.telefono)}
                                    disabled={!telefonoNormalizado}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        borderColor: '#25D366',
                                        color: telefonoNormalizado ? '#128C43' : 'text.disabled',
                                    }}
                                >
                                    {item.label}{telefonoNormalizado ? ` - ${item.telefono}` : ' - pendiente'}
                                </Button>
                            );
                        })}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.5 }}>
                    <Button onClick={() => setOpenWhatsapp(false)} variant="outlined">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
